'use client';
import { css } from 'styled-components';
import { fromMd } from '../../constants';
import { themeTypography } from '../../themes/default/themeTypography';
import { skeletonAnimationCss } from '../Skeleton';
const getTypeStyleCssFunction = (props) => {
    return css(o => {
        const typographyTokens = props.type === 'headline'
            ? o.theme.typography.headline[props.name]
            : o.theme.typography.body[props.name];
        const overrideAttributes = {
            ...(o.$isUppercase && o.theme.typography.effect.uppercase
                ? o.theme.typography.effect.uppercase
                : {}),
            ...(o.$fontWeight ? { fontWeight: o.$fontWeight } : {}),
        };
        const fromMdAttributes = typographyTokens.fromMd.fontSize !== typographyTokens.untilMd.fontSize
            ? {
                [fromMd]: {
                    ...typographyTokens.fromMd,
                    ...overrideAttributes,
                },
            }
            : {};
        return {
            ...typographyTokens.untilMd,
            ...overrideAttributes,
            ...fromMdAttributes,
        };
    });
};
export const typeStyleMap = {
    headlineExtraLarge: getTypeStyleCssFunction({ type: 'headline', name: 'extraLarge' }),
    headlineLarge: getTypeStyleCssFunction({ type: 'headline', name: 'large' }),
    headlineMedium: getTypeStyleCssFunction({ type: 'headline', name: 'medium' }),
    headlineSmall: getTypeStyleCssFunction({ type: 'headline', name: 'small' }),
    headlineExtraSmall: getTypeStyleCssFunction({ type: 'headline', name: 'extraSmall' }),
    headlineExtraExtraSmall: getTypeStyleCssFunction({ type: 'headline', name: 'extraExtraSmall' }),
    headlineMicro: getTypeStyleCssFunction({ type: 'headline', name: 'micro' }),
    eyebrow: css `
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.15em;
    line-height: 1.7;

    ${fromMd} {
      font-size: 14px;
    }
  `,
    inherit: css(o => ({
        font: 'inherit',
        letterSpacing: 'inherit',
        ...(o.$isUppercase && o.theme.typography.effect.uppercase
            ? o.theme.typography.effect.uppercase
            : {}),
        ...(o.$fontWeight ? { fontWeight: o.$fontWeight } : {}),
    })),
    bodyDefault: getTypeStyleCssFunction({ type: 'body', name: 'default' }),
    bodyLarge: getTypeStyleCssFunction({ type: 'body', name: 'large' }),
    bodyMedium: getTypeStyleCssFunction({ type: 'body', name: 'medium' }),
    bodySmall: getTypeStyleCssFunction({ type: 'body', name: 'small' }),
    bodyMicro: getTypeStyleCssFunction({ type: 'body', name: 'micro' }),
    bodyNano: getTypeStyleCssFunction({ type: 'body', name: 'nano' }),
    bodyTiny: getTypeStyleCssFunction({ type: 'body', name: 'tiny' }),
};
const getTypeStyleSkeletonCssFunction = (props) => {
    return css(o => {
        const typographyTokens = props.type === 'headline'
            ? o.theme.typography.headline[props.name]
            : o.theme.typography.body[props.name];
        return {
            display: 'block',
            height: typographyTokens.untilMd.fontSize,
            marginTop: `calc((${typographyTokens.untilMd.lineHeight} - 1) * ${typographyTokens.untilMd.fontSize})`,
            [fromMd]: {
                height: typographyTokens.fromMd.fontSize,
                marginTop: `calc((${typographyTokens.fromMd.lineHeight} - 1) * ${typographyTokens.fromMd.fontSize})`,
            },
        };
    }, skeletonAnimationCss);
};
export const typeStyleSkeletonMap = {
    headlineExtraLarge: getTypeStyleSkeletonCssFunction({ type: 'headline', name: 'extraLarge' }),
    headlineLarge: getTypeStyleSkeletonCssFunction({ type: 'headline', name: 'large' }),
    headlineMedium: getTypeStyleSkeletonCssFunction({ type: 'headline', name: 'medium' }),
    headlineSmall: getTypeStyleSkeletonCssFunction({ type: 'headline', name: 'small' }),
    headlineExtraSmall: getTypeStyleSkeletonCssFunction({ type: 'headline', name: 'extraSmall' }),
    headlineExtraExtraSmall: getTypeStyleSkeletonCssFunction({
        type: 'headline',
        name: 'extraExtraSmall',
    }),
    headlineMicro: getTypeStyleSkeletonCssFunction({ type: 'headline', name: 'micro' }),
    eyebrow: css `
    ${skeletonAnimationCss};
    display: block;
    height: 12px;
    margin-top: calc(0.7 * 12px);

    ${fromMd} {
      height: 12px;
      margin-top: calc(0.7 * 14px);
    }
  `,
    inherit: css ``,
    bodyDefault: getTypeStyleSkeletonCssFunction({ type: 'body', name: 'default' }),
    bodyLarge: getTypeStyleSkeletonCssFunction({ type: 'body', name: 'large' }),
    bodyMedium: getTypeStyleSkeletonCssFunction({ type: 'body', name: 'medium' }),
    bodySmall: getTypeStyleSkeletonCssFunction({ type: 'body', name: 'small' }),
    bodyMicro: getTypeStyleSkeletonCssFunction({ type: 'body', name: 'micro' }),
    bodyNano: getTypeStyleSkeletonCssFunction({ type: 'body', name: 'nano' }),
    bodyTiny: getTypeStyleSkeletonCssFunction({ type: 'body', name: 'tiny' }),
};
export const getTypeStyleProperties = (styleName) => {
    if (styleName === 'inherit') {
        return 'styleName: inherit';
    }
    if (styleName === 'eyebrow') {
        return `styleName: eyebrow

Mobile (< md):
  font-size: 12px
  font-weight: 700
  line-height: 1.7
  letter-spacing: 0.15em

Desktop (≥ md):
  font-size: 14px
  font-weight: 700
  line-height: 1.7
  letter-spacing: 0.15em`;
    }
    const type = styleName.startsWith('headline') ? 'headline' : 'body';
    const nameWithoutPrefix = styleName.slice(type.length);
    const name = nameWithoutPrefix.charAt(0).toLowerCase() + nameWithoutPrefix.slice(1);
    const tokens = type === 'headline'
        ? themeTypography.headline[name]
        : themeTypography.body[name];
    if (!tokens) {
        return 'Properties not found';
    }
    const { untilMd, fromMd } = tokens;
    const result = `styleName: ${styleName}

Mobile (< md):
  font-size: ${untilMd.fontSize}
  font-weight: ${untilMd.fontWeight}
  line-height: ${untilMd.lineHeight}
  letter-spacing: ${untilMd.letterSpacing}

Desktop (≥ md):
  font-size: ${fromMd.fontSize}
  font-weight: ${fromMd.fontWeight}
  line-height: ${fromMd.lineHeight}
  letter-spacing: ${fromMd.letterSpacing}`;
    return result;
};
//# sourceMappingURL=fixtures.js.map